import React, { useState } from "react";
import { useRequest } from "../../../shared/hooks/useRequest";
import LinkedAccountsSummary from "./components/LinkedAccountsSummary";
import { useHistory } from "react-router-dom";
import { Alert, Button, ButtonVariant, Card, Text } from "@merge-api/merge-javascript-shared";
import { fetchWithAuth } from "../../../../api-client/APIClient";
import { LinkedAccount, LinkedAccountMetrics } from "../../../../models/Entities";
import {
  navigateToLinkedAccountsTablePage,
  PROD_LINKED_ACCOUNTS_PATH,
  TEST_LINKED_ACCOUNTS_PATH,
  useQuery,
} from "../../../../router/RouterUtils";
import PortalPageHeaderWrapper from "../../../portal/PortalPageHeaderWrapper";
import PaginationFooter from "../../../shared/PaginationFooter";
import { showErrorToast, showSuccessToast } from "../../../shared/Toasts";
import LinkedAccountDeletionModal from "../shared/LinkedAccountDeletionModal";
import CreateLinkedAccountButton from "../CreateLinkedAccountButton";
import LinkedAccountsFilterBar from "./components/LinkedAccountsFilterBar/LinkedAccountsFilterBar";
import LinkedAccountsTable from "./components/LinkedAccountsTable/LinkedAccountsTable";
import { useLinkedAccountsFilters } from "./hooks/useLinkedAccountsFilters";
import { useLinkedAccountsList } from "./hooks/useLinkedAccountsList";
import { buildLinkedAccountsParams } from "./utils/buildLinkedAccountsParams";

interface Props {
  isTestAccounts: boolean;
}

const LinkedAccountsRedesignPage = ({ isTestAccounts }: Props) => {
  const [linkedAccountToDelete, setLinkedAccountToDelete] = useState<LinkedAccount | undefined>();

  const history = useHistory();
  const query = useQuery();
  const { filters, setFilters, filterSearch } = useLinkedAccountsFilters();
  const filterQuery = filterSearch ? `?${filterSearch}` : "";
  const completeProductionDuplicatesOf = query.get("complete_production_duplicates_of");
  const {
    results,
    hasNext,
    hasPrevious,
    onNext,
    onPrevious,
    onRemove,
    refetch,
    isLoading,
    errorStatus,
  } = useLinkedAccountsList({ filters, isTestAccounts, completeProductionDuplicatesOf });

  const {
    data: metrics,
    loading: metricsLoading,
    error: metricsError,
    refetch: refetchMetrics,
  } = useRequest<LinkedAccountMetrics>({
    path: `/integrations/linked-accounts/detailed-counts?${buildLinkedAccountsParams({
      filters: { ...filters, status: null },
      isTestAccounts,
      completeProductionDuplicatesOf,
    })}`,
  });
  const permissionDenied =
    errorStatus === 403 ||
    (typeof metricsError === "object" &&
      metricsError !== null &&
      "status" in metricsError &&
      metricsError.status === 403);
  const controlsDisabled = permissionDenied;
  const permissionMessage = "You do not have permission to access Linked Accounts";
  const refreshAccounts = () => {
    refetch();
    refetchMetrics();
  };

  const showDuplicatesOnly = !!completeProductionDuplicatesOf;

  const onIgnoreDuplicatesClick = () => {
    fetchWithAuth({
      path: `/integrations/linked-accounts/${completeProductionDuplicatesOf}/ignore-duplicates`,
      method: "POST",
      onResponse: () => {
        showSuccessToast("Successfully ignored accounts with these credentials");
        navigateToLinkedAccountsTablePage(history);
      },
      onError: () => showErrorToast("Failed to ignore accounts with these credentials"),
    });
  };

  const deleteLinkedAccount = () => {
    if (!linkedAccountToDelete) return;

    fetchWithAuth({
      path: `/integrations/linked-accounts/${linkedAccountToDelete.id}`,
      method: "DELETE",
      onResponse: () => {
        showSuccessToast("Deleted Linked Account");
        refetchMetrics();
        const indexToRemove = results?.findIndex(
          (linkedAccount) => linkedAccount.id === linkedAccountToDelete.id,
        );
        if (indexToRemove !== undefined && indexToRemove >= 0) onRemove(indexToRemove);
        setLinkedAccountToDelete(undefined);
      },
      onError: () => {
        showErrorToast("Failed to delete Linked Account. Please try again.");
        setLinkedAccountToDelete(undefined);
      },
    });
  };

  return (
    <PortalPageHeaderWrapper
      title="Linked Accounts"
      subtitle="Integrations between your users' accounts and third-party platforms"
      subtabs={[
        { label: "Production", destination: `${PROD_LINKED_ACCOUNTS_PATH}${filterQuery}` },
        { label: "Test", destination: `${TEST_LINKED_ACCOUNTS_PATH}${filterQuery}` },
      ]}
      buttons={
        <CreateLinkedAccountButton
          onRefetch={refreshAccounts}
          isTestAccounts={isTestAccounts}
          disabled={controlsDisabled}
          disabledReason={controlsDisabled ? permissionMessage : undefined}
        />
      }
    >
      <LinkedAccountDeletionModal
        isModalOpen={linkedAccountToDelete !== undefined}
        deleteLinkedAccount={deleteLinkedAccount}
        closeModal={() => setLinkedAccountToDelete(undefined)}
        linkedAccount={linkedAccountToDelete}
      />
      {showDuplicatesOnly && !permissionDenied && (
        <Alert className="mt-2 mb-6">
          <div className="flex flex-row w-full items-center justify-between gap-2">
            <div className="flex flex-col">
              <Text variant="h6">
                These Linked Accounts are configured using the same credentials
              </Text>
              <Text>
                If this is intentional, press &quot;Ignore&quot; to dismiss warnings for these
                accounts. Otherwise, delete unwanted Linked Accounts to avoid unnecessary syncing.
              </Text>
            </div>
            <Button
              size="sm"
              disabled={controlsDisabled}
              onClick={onIgnoreDuplicatesClick}
              variant={ButtonVariant.TertiaryWhite}
            >
              Ignore
            </Button>
          </div>
        </Alert>
      )}
      <LinkedAccountsFilterBar
        filters={filters}
        onFiltersChange={setFilters}
        disabled={showDuplicatesOnly || controlsDisabled}
        disabledReason={controlsDisabled ? permissionMessage : undefined}
      />
      <LinkedAccountsSummary
        metrics={metrics}
        loading={metricsLoading || (!metrics && !metricsError)}
        permissionDenied={permissionDenied}
      />
      <Card className="mb-9">
        <LinkedAccountsTable
          linkedAccounts={results}
          isLoading={isLoading}
          permissionDenied={permissionDenied}
          hasError={errorStatus !== undefined}
          onDeleteClick={setLinkedAccountToDelete}
        />
      </Card>
      {!permissionDenied && errorStatus === undefined && (
        <PaginationFooter
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          onPreviousClick={onPrevious}
          onNextClick={onNext}
        />
      )}
    </PortalPageHeaderWrapper>
  );
};

export default LinkedAccountsRedesignPage;
